<script setup lang="ts">
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import type { PageBlock } from "@/views/blocks/PageBlock.interface";
import EditableElement from "@/views/page/EditableElement.vue";
import { computed } from "vue";
import type { WithImageTitlesProps } from "./WithImageTitlesProps";

// Import shared components
import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import FullSpacing from "@/views/blocks/shared/section/layout/spacing/FullSpacing.vue";
import Image from "@/views/blocks/shared/section/media/image/Image.vue";
import Heading from "@/views/blocks/shared/section/typography/heading/Heading.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";

const props = defineProps<{
    block: PageBlock<WithImageTitlesProps>;
    pageColors: CssVarColors;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <Section :id="block.webId" :backgroundColor="styleValues.background">
        <FullSpacing class="overflow-hidden">
            <div class="max-w-2xl lg:max-w-7xl mx-auto">
                <div class="">
                    <EditableElement
                        v-if="!dataValues.tag.isHidden"
                        :blockId="block.id"
                        fieldName="tag"
                    >
                        <Heading
                            :text="dataValues.tag.data.text"
                            :tag="dataValues.tag.data.tag"
                            :textColor="styleValues.brand"
                            class="text-base/7 mb-2"
                        />
                    </EditableElement>

                    <EditableElement
                        v-if="!dataValues.title.isHidden"
                        :blockId="block.id"
                        fieldName="title"
                    >
                        <Heading
                            :text="dataValues.title.data.text"
                            :tag="dataValues.title.data.tag"
                            :textColor="styleValues.text"
                            class="text-3xl sm:text-4xl lg:text-5xl mb-6"
                        />
                    </EditableElement>

                    <EditableElement
                        v-if="!dataValues.description.isHidden"
                        :blockId="block.id"
                        fieldName="description"
                    >
                        <Paragraph
                            :text="dataValues.description.data.text"
                            :textColor="styleValues.text"
                            class="text-xl/8 max-w-3xl xl:max-w-none opacity-70"
                        />
                    </EditableElement>
                </div>

                <div class="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-16">
                    <!-- Secondary heading and descriptions -->
                    <div class="lg:pr-8">
                        <EditableElement
                            v-if="!dataValues.secondaryTitle.isHidden"
                            :blockId="block.id"
                            fieldName="secondaryTitle"
                        >
                            <Heading
                                :text="dataValues.secondaryTitle.data.text"
                                :tag="dataValues.secondaryTitle.data.tag"
                                :textColor="styleValues.text"
                                class="text-2xl mb-6"
                            />
                        </EditableElement>

                        <EditableElement
                            v-if="!dataValues.secondaryDescriptionOne.isHidden"
                            :blockId="block.id"
                            fieldName="secondaryDescriptionOne"
                        >
                            <Paragraph
                                :text="dataValues.secondaryDescriptionOne.data.text"
                                :textColor="styleValues.text"
                                class="text-base/7 opacity-70"
                            />
                        </EditableElement>

                        <EditableElement
                            v-if="!dataValues.secondaryDescriptionTwo.isHidden"
                            :blockId="block.id"
                            fieldName="secondaryDescriptionTwo"
                        >
                            <Paragraph
                                :text="dataValues.secondaryDescriptionTwo.data.text"
                                :textColor="styleValues.text"
                                class="text-base/7 mt-8 opacity-70"
                            />
                        </EditableElement>
                    </div>

                    <!-- Image grid -->
                    <div class="pt-16 -mx-8 sm:-mx-16 lg:-ml-0 xl:mr-auto lg:row-span-2">
                        <div class="grid grid-cols-2 gap-4 lg:gap-8 sm:grid-cols-4 lg:grid-cols-2">
                            <EditableElement
                                v-for="img in dataValues.images.data.items"
                                :key="img.id"
                                :blockId="block.id"
                                :fieldName="`images_${img.id}`"
                                :hideable="false"
                            >
                                <Image
                                    :src="img.src"
                                    :alt="img.alt"
                                    class="aspect-[1/1] object-cover rounded-lg shadow-xl even:-mt-8 even:lg:-mt-40"
                                    :width="296"
                                    :height="296"
                                />
                            </EditableElement>
                        </div>
                    </div>

                    <!-- Statistics -->
                    <div class="mt-12">
                        <EditableElement
                            v-if="!dataValues.statsTitle.isHidden"
                            :blockId="block.id"
                            fieldName="statsTitle"
                        >
                            <Heading
                                :text="dataValues.statsTitle.data.text"
                                :tag="dataValues.statsTitle.data.tag"
                                :textColor="styleValues.text"
                                class="text-base/7 mb-6 opacity-70"
                            />
                        </EditableElement>

                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 pt-6">
                            <EditableElement
                                v-for="stat in dataValues.stats.data.items"
                                :key="stat.id"
                                :blockId="block.id"
                                :fieldName="`stats_${stat.id}`"
                                :hideable="false"
                            >
                                <div class="space-y-2 pb-4">
                                    <Paragraph
                                        :text="stat.title"
                                        :textColor="styleValues.text"
                                        class="text-6xl font-semibold"
                                    />
                                    <Paragraph
                                        :text="stat.description"
                                        :textColor="styleValues.text"
                                        class="text-sm/6 opacity-70"
                                    />
                                </div>
                            </EditableElement>
                        </div>
                    </div>
                </div>
            </div>
        </FullSpacing>
    </Section>
</template>
